// src/pages/notifications/ChannelDetailPage.tsx
// Channel detail view with type-specific config.

import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useNotificationsChannel, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardContent, Alert, Badge, ErrorAlert,
} from '@react/components';
import { StatusBadge } from '../../components/StatusBadge';
import { formatChannelType, formatChannelScope, formatChannelHealth, getChannelConfigKey } from '../../features/notifications/logic';
import { NotificationIcon } from '../../components/NotificationIcon';
import type { NotificationChannel } from '@core/types/notifications/NotificationChannel';

export const ChannelDetailPage = observer(() => {
  const store = useNotificationsChannel();
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) store.GetChannel(id);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [healthStatus, setHealthStatus] = useState<string | null>(null);
  const [checkingHealth, setCheckingHealth] = useState(false);

  const channel = store.currentItem as NotificationChannel | null;

  useEffect(() => {
    if (channel?.entity?.id) {
      setCheckingHealth(true);
      store.GetChannelHealth(channel.entity.id).then((resp: unknown) => {
        const r = resp as { status?: string } | null;
        setHealthStatus(r?.status ?? null);
      }).catch(() => {}).finally(() => setCheckingHealth(false));
    }
  }, [channel?.entity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (store.isLoadingItem) return <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>;
  if (!channel) return <Alert variant="error">{t('common.notFound')}</Alert>;

  const chType = formatChannelType(channel.type ?? '');
  const scope = formatChannelScope(channel.scope ?? '');
  const health = healthStatus ? formatChannelHealth(healthStatus) : null;

  const handleDelete = async () => {
    if (!confirm(t('notifications.deleteChannelConfirm'))) return;
    await store.DeleteChannel(channel.entity?.id ?? '');
    navigate('/notifications/channels');
  };

  const handleTest = async () => {
    try {
      await store.TestChannel(channel.entity?.id ?? '', {} as never);
    } catch {
      // handled by store
    }
  };

  const Field = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className={`mt-1 text-sm text-foreground sm:col-span-2 sm:mt-0 ${mono ? 'font-mono' : ''}`}>{value || t('common.notSet')}</dd>
    </div>
  );

  // Get channel-specific config
  const configKey = getChannelConfigKey(channel.type ?? '');
  const config = configKey ? (channel as unknown as Record<string, unknown>)[configKey] : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/notifications/channels" className="text-sm text-primary hover:text-primary/80">{t('notifications.channels')}</Link>
          <h1 className="text-2xl font-semibold text-foreground mt-1">{channel.name}</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleTest}>{t('notifications.testChannel')}</Button>
          <Link to={`/notifications/channels/${channel.entity?.id}/edit`}><Button variant="secondary">{t('common.edit')}</Button></Link>
          <Button variant="danger" onClick={handleDelete}>{t('common.delete')}</Button>
        </div>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <CardHeader><CardTitle>{t('notifications.channelDetails')}</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <Field label={t('common.id')} value={channel.entity?.id} mono />
            <Field label={t('common.name')} value={channel.name} />
            <Field label={t('common.description')} value={channel.description} />
            <Field label={t('notifications.channelType')} value={<span className="inline-flex items-center gap-1.5"><NotificationIcon name={chType.icon} className="h-4 w-4" />{t(chType.labelKey)}</span>} />
            <Field label={t('notifications.scope')} value={t(scope.labelKey)} />
            <Field label={t('notifications.enabled')} value={<Badge variant={channel.is_enabled ? 'success' : 'secondary'}>{channel.is_enabled ? t('common.yes') : t('common.no')}</Badge>} />
            <Field label={t('notifications.default')} value={<Badge variant={channel.is_default ? 'primary' : 'secondary'}>{channel.is_default ? t('common.yes') : t('common.no')}</Badge>} />
            {health && (
              <Field label={t('notifications.healthStatus')} value={<StatusBadge variant={health.variant} label={t(health.labelKey)} />} />
            )}
            {checkingHealth && (
              <Field label={t('notifications.healthStatus')} value={<span className="text-muted-foreground">{t('common.loading')}</span>} />
            )}
            <Field label={t('notifications.channelPriority')} value={channel.priority} />
            <Field label={t('common.createdAt')} value={channel.created_at} />
            <Field label={t('common.updatedAt')} value={channel.updated_at} />
          </dl>
        </CardContent>
      </Card>

      {config ? (
        <Card>
          <CardHeader><CardTitle>{t('notifications.channelConfig')}</CardTitle></CardHeader>
          <CardContent>
            <pre className="text-xs text-muted-foreground overflow-auto">{JSON.stringify(config, null, 2)}</pre>
          </CardContent>
        </Card>
      ) : null}

      {channel.rate_limits && (
        <Card>
          <CardHeader><CardTitle>{t('notifications.rateLimits')}</CardTitle></CardHeader>
          <CardContent><pre className="text-xs text-muted-foreground overflow-auto">{JSON.stringify(channel.rate_limits, null, 2)}</pre></CardContent>
        </Card>
      )}

      {channel.failover_config && (
        <Card>
          <CardHeader><CardTitle>{t('notifications.failoverConfig')}</CardTitle></CardHeader>
          <CardContent><pre className="text-xs text-muted-foreground overflow-auto">{JSON.stringify(channel.failover_config, null, 2)}</pre></CardContent>
        </Card>
      )}

      {channel.metadata && Object.keys(channel.metadata).length > 0 && (
        <Card>
          <CardHeader><CardTitle>{t('common.metadata')}</CardTitle></CardHeader>
          <CardContent><pre className="text-xs text-muted-foreground overflow-auto">{JSON.stringify(channel.metadata, null, 2)}</pre></CardContent>
        </Card>
      )}
    </div>
  );
});

export default ChannelDetailPage;
